import React, { useState } from 'react';

/* =========================================================================
   SourceInfoCard — collapsible card showing climate data source details
   ========================================================================= */

const styles = {
  card: {
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: 8,
    marginBottom: 8,
    overflow: 'hidden',
    transition: 'border-color 0.15s',
    cursor: 'default',
  },
  cardSelected: {
    borderColor: '#3b82f6',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 12px',
  },
  colorDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    flexShrink: 0,
  },
  name: {
    color: '#e2e8f0',
    fontWeight: 600,
    fontSize: 14,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  typeBadge: {
    fontSize: 11,
    padding: '2px 6px',
    borderRadius: 4,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  satelliteBadge: {
    background: '#854d0e',
    color: '#fde68a',
  },
  reanalysisBadge: {
    background: '#1e3a5f',
    color: '#93c5fd',
  },
  yearRange: {
    color: '#94a3b8',
    fontSize: 12,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  spacer: { flex: 1 },
  checkmark: {
    color: '#3b82f6',
    fontSize: 16,
    flexShrink: 0,
    cursor: 'pointer',
    padding: '0 4px',
  },
  expandBtn: {
    background: 'none',
    border: 'none',
    color: '#94a3b8',
    fontSize: 14,
    cursor: 'pointer',
    padding: '0 4px',
    flexShrink: 0,
    lineHeight: 1,
  },
  subRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '0 12px 8px 30px',
    fontSize: 12,
    color: '#94a3b8',
  },
  details: {
    borderTop: '1px solid #334155',
    padding: '10px 12px 12px',
    fontSize: 13,
    color: '#cbd5e1',
    lineHeight: 1.55,
  },
  detailLabel: {
    color: '#64748b',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginTop: 10,
    marginBottom: 2,
  },
  detailText: {
    color: '#e2e8f0',
    margin: 0,
  },
  variableTag: {
    display: 'inline-block',
    background: '#334155',
    color: '#cbd5e1',
    fontSize: 11,
    padding: '2px 6px',
    borderRadius: 4,
    marginRight: 4,
    marginBottom: 4,
  },
  statusDot: {
    display: 'inline-block',
    width: 7,
    height: 7,
    borderRadius: '50%',
    marginRight: 5,
    verticalAlign: 'middle',
  },
  notIntegratedBadge: {
    display: 'inline-block',
    fontSize: 11,
    padding: '2px 6px',
    borderRadius: 4,
    background: '#7f1d1d',
    color: '#fca5a5',
    marginTop: 6,
  },
};

function statusColor(status) {
  if (status === 'live') return '#22c55e';
  if (status === 'cached') return '#eab308';
  return '#94a3b8'; // fallback / unknown
}

function formatDate(isoStr) {
  if (!isoStr) return '—';
  try {
    return new Date(isoStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return isoStr;
  }
}

export default function SourceInfoCard({ source, selected, onClick }) {
  const [expanded, setExpanded] = useState(false);

  if (!source) return null;

  const {
    color, name, type, year_min, year_max,
    resolution_km, spatial_resolution_description,
    variables, distance_km,
    provider, satellite, description,
    best_for, accuracy_notes, limitations,
    nearest_lat, nearest_lon,
    availability_status, availability_checked_at,
    free, integrated,
  } = source;

  const varCount = Array.isArray(variables) ? variables.length : 0;
  const typeLabel = type === 'satellite' ? 'Satellite' : type === 'reanalysis' ? 'Reanalysis' : type || '';
  const typeBadgeStyle = {
    ...styles.typeBadge,
    ...(type === 'satellite' ? styles.satelliteBadge : styles.reanalysisBadge),
  };

  const handleExpand = (e) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  };

  const handleSelect = (e) => {
    e.stopPropagation();
    if (onClick) onClick(source);
  };

  return (
    <div style={{ ...styles.card, ...(selected ? styles.cardSelected : {}) }}>
      {/* ---- Collapsed header ---- */}
      <div style={styles.header}>
        <span style={{ ...styles.colorDot, background: color || '#64748b' }} />
        <span style={styles.name}>{name}</span>
        {typeLabel && <span style={typeBadgeStyle}>{typeLabel}</span>}
        {(year_min != null || year_max != null) && (
          <span style={styles.yearRange}>
            {year_min ?? '?'}–{year_max ?? '?'}
          </span>
        )}
        <span style={styles.spacer} />
        <span
          style={styles.checkmark}
          onClick={handleSelect}
          title={selected ? 'Deselect source' : 'Select source'}
        >
          {selected ? '✓' : '○'}
        </span>
        <button
          style={styles.expandBtn}
          onClick={handleExpand}
          title={expanded ? 'Collapse' : 'Expand details'}
        >
          {expanded ? '▲' : '▼'}
        </button>
      </div>

      {/* ---- Sub-row: resolution, vars, distance ---- */}
      <div style={styles.subRow}>
        {resolution_km != null && <span>{resolution_km} km</span>}
        {spatial_resolution_description && !resolution_km && (
          <span>{spatial_resolution_description}</span>
        )}
        {varCount > 0 && <span>{varCount} variable{varCount !== 1 ? 's' : ''}</span>}
        {distance_km != null && (
          <span>{distance_km < 1 ? '<1' : Math.round(distance_km)} km from site</span>
        )}
        {free === true && <span style={{ color: '#22c55e' }}>Free</span>}
      </div>

      {/* ---- Expanded details ---- */}
      {expanded && (
        <div style={styles.details}>
          {provider && (
            <>
              <div style={styles.detailLabel}>Provider</div>
              <p style={styles.detailText}>{provider}</p>
            </>
          )}

          {satellite && (
            <>
              <div style={styles.detailLabel}>Satellite</div>
              <p style={styles.detailText}>{satellite}</p>
            </>
          )}

          {description && (
            <>
              <div style={styles.detailLabel}>Description</div>
              <p style={styles.detailText}>{description}</p>
            </>
          )}

          {best_for && (
            <>
              <div style={styles.detailLabel}>Best for</div>
              <p style={styles.detailText}>{best_for}</p>
            </>
          )}

          {accuracy_notes && (
            <>
              <div style={styles.detailLabel}>Accuracy</div>
              <p style={styles.detailText}>{accuracy_notes}</p>
            </>
          )}

          {limitations && (
            <>
              <div style={styles.detailLabel}>Limitations</div>
              <p style={styles.detailText}>{limitations}</p>
            </>
          )}

          {varCount > 0 && (
            <>
              <div style={styles.detailLabel}>Variables</div>
              <div>
                {variables.map((v) => (
                  <span key={v} style={styles.variableTag}>{v}</span>
                ))}
              </div>
            </>
          )}

          {(nearest_lat != null || nearest_lon != null) && (
            <>
              <div style={styles.detailLabel}>Nearest grid point</div>
              <p style={styles.detailText}>
                {nearest_lat != null ? nearest_lat.toFixed(3) : '?'}°,{' '}
                {nearest_lon != null ? nearest_lon.toFixed(3) : '?'}°
                {distance_km != null && (
                  <span style={{ color: '#94a3b8' }}>
                    {' '}({distance_km < 1 ? '<1' : Math.round(distance_km)} km away)
                  </span>
                )}
              </p>
            </>
          )}

          {availability_status && (
            <>
              <div style={styles.detailLabel}>Availability</div>
              <p style={styles.detailText}>
                <span
                  style={{ ...styles.statusDot, background: statusColor(availability_status) }}
                />
                {availability_status}
                {availability_checked_at && (
                  <span style={{ color: '#64748b', fontSize: 11, marginLeft: 8 }}>
                    checked {formatDate(availability_checked_at)}
                  </span>
                )}
              </p>
            </>
          )}

          {integrated === false && (
            <span style={styles.notIntegratedBadge}>Not integrated</span>
          )}
        </div>
      )}
    </div>
  );
}
